import React from 'react';
import intl from 'react-intl-universal';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Select from 'react-select';
import MDTypography from 'components/MDTypography';
import { Filters, SearchInput } from './Styles';
import { getUserFullName } from 'utils/name';
import { UserProptypes } from 'shared/propTypes/userType';
import { FilterProptypes } from 'shared/propTypes/filterType';
import { QuotaProptypes } from 'shared/propTypes/quotaType';
import logger from 'utils/logger';

const log = logger.createLogger('ProjectBoardFilters');

const propTypes = {
  sourceOrganizations: PropTypes.arrayOf(PropTypes.shape(QuotaProptypes)),
  doctors: PropTypes.arrayOf(PropTypes.shape(UserProptypes)),
  filters: PropTypes.shape(FilterProptypes),
  mergeFilters: PropTypes.func.isRequired,
};

const customStyles = {
  control: (provided, _) => ({
    ...provided,
    minHeight: '40px', // Custom height
    height: '40px',
  }),

  valueContainer: (provided, _) => ({
    ...provided,
    height: '40px',
  }),

  input: (provided, _) => ({
    ...provided,
    margin: '-1px',
  }),

  indicatorsContainer: (provided, _) => ({
    ...provided,
    height: '40px',
  }),

  multiValue: (provided, _) => ({
    ...provided,
    height: '30px', // Set the height of the chip
    display: 'flex',
    alignItems: 'center', // This ensures vertical centering of the content
    justifyContent: 'center', // This centers the content horizontally (if needed)
  }),
  multiValueLabel: (provided, _) => ({
    ...provided,
    fontSize: '14px',
  }),
  multiValueRemove: (provided, _) => ({
    ...provided,
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  placeholder: (provided, _) => ({
    ...provided,
    fontSize: '14px',
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: '16px',
    fontWeight: state.isSelected ? 'bold' : 'normal',
  }),
};

const ProjectBoardFilters = ({
  sourceOrganizations = [],
  doctors = [],
  filters,
  mergeFilters,
}) => {
  const { searchTerm } = filters;
  const doctorsOptions = doctors.map((u) => ({
    value: u.id,
    label: getUserFullName(u.firstName, u.lastName),
  }));

  const onSelectDoctor = (options) => {
    const inputUserIds = options.map((o) => o.value);
    mergeFilters({ userIds: inputUserIds });
  };

  const sourceOrgOptions = sourceOrganizations.map((item) => ({
    value: item.srcOrgId,
    label: item.srcOrg.name,
  }));

  const onSelectOrganization = (options) => {
    const inputOrgIds = options.map((o) => o.value);
    mergeFilters({ srcOrgIds: inputOrgIds });
  };

  return (
    <Filters data-testid="board-filters">
      <Stack
        direction="row"
        alignItems="center"
        display="flex"
        spacing={{ xs: 3 }}>
        <SearchInput
          data-testid="kanban-filter-search-input"
          icon="search"
          placeholder={intl.get('kanban_filter_text_free_search')}
          value={searchTerm}
          onChange={(value) => mergeFilters({ searchTerm: value })}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <MDTypography variant="h6" marginLeft="20px">
            {intl.get('kanban_filter_text_filter_by_doctor')}
          </MDTypography>
          <Select
            classNamePrefix="select"
            isClearable
            isSearchable
            isMulti
            placeholder={intl.get('kanban_filter_text_click_to_select')}
            options={doctorsOptions}
            onChange={onSelectDoctor}
            styles={customStyles}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <MDTypography variant="h6" marginLeft="20px">
            {intl.get('kanban_filter_text_filter_by_organization')}
          </MDTypography>
          <Select
            classNamePrefix="select"
            isClearable={true}
            isSearchable
            isMulti
            placeholder={intl.get('kanban_filter_text_click_to_select')}
            options={sourceOrgOptions}
            onChange={onSelectOrganization}
            styles={customStyles}
          />
        </Stack>
      </Stack>
    </Filters>
  );
};

ProjectBoardFilters.propTypes = propTypes;

export default ProjectBoardFilters;
